import { ApolloLink } from '@apollo/client';
import { mainClient } from 'apollo/client';
import { createAuthenticationLink, errorLink, mainLink } from 'apollo/link';
import Error404 from 'components/desktop/error-404';
import MobileError404 from 'components/mobile/error-404';
import { AtmnhaAuthenticationModel } from 'components/providers/authentication-provider';
import {
  DEFAULT_DESCRIPTION,
  DEFAULT_OG_IMAGE,
  DEFAULT_TITLE,
  MAIN_DOMAIN,
  SITE_MAP,
} from 'globalConstants';
import {
  ApprovalStatusEnumType,
  GET_REAL_ESTATES,
  GetRealEstatesData,
  GetRealEstatesVariables,
  ListRealEstate,
  PostStatusType,
  TradingStatus,
  USER_INFO,
  UserInfoData,
  UserInfoVariables,
  UserUnion,
} from 'graphql/main/queries';
import { isEmpty, isEqual, isString, lte, toNumber, toString } from 'lodash';
import { GetServerSideProps, NextPage } from 'next';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { Context, Fragment, createContext } from 'react';
import { detectDeviceByUserAgent, parseSortParamFromStringToObject } from 'utils';

const DetailBroker = dynamic(() => import('components/desktop/detail-broker'));
const MobileDetailBroker = dynamic(() => import('components/mobile/detail-broker'));

const DEFAULT_LIMIT_CARD_ITEM = 28;

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { req, params, query } = context;
  const { headers, cookies } = req;
  const authenticationJSONString = toString(cookies['atmnha_authentication_cookie']);
  if (authenticationJSONString) {
    const { accessToken } = JSON.parse(authenticationJSONString) as AtmnhaAuthenticationModel;
    if (accessToken) {
      const authLink = createAuthenticationLink(accessToken);
      mainClient.setLink(ApolloLink.from([errorLink, authLink, mainLink]));
    }
  }
  const device = detectDeviceByUserAgent(headers['user-agent']);
  let pageData = null;
  const { [SITE_MAP.LIST_BROKER.DETAIL_BROKER]: detailBrokerPathname } = params ?? {};
  const { data: userInfoData } = await mainClient.query<UserInfoData, UserInfoVariables>({
    query: USER_INFO,
    variables: {
      id: isString(detailBrokerPathname) ? detailBrokerPathname : '',
    },
  });
  const { userInfo: user } = userInfoData;

  if (user) {
    const { page: pageQuery, sort: sortQuery } = query;
    const { data: getRealEstatesData } = await mainClient.query<
      GetRealEstatesData,
      GetRealEstatesVariables
    >({
      query: GET_REAL_ESTATES,
      variables: {
        creator: toString(detailBrokerPathname),
        approvalStatus: ApprovalStatusEnumType.approved,
        postStatus: [PostStatusType.active],
        tradingStatus: [TradingStatus.trading],
        limit: DEFAULT_LIMIT_CARD_ITEM,
        page: isNaN(toNumber(pageQuery)) || lte(toNumber(pageQuery), 0) ? 1 : toNumber(pageQuery),
        sort:
          isString(sortQuery) && !isEmpty(sortQuery)
            ? parseSortParamFromStringToObject(sortQuery)
            : { createdAt: -1 },
      },
    });
    const { getRealEstates: realEstatesWithPagination } = getRealEstatesData;

    pageData = {
      user: user ?? null,
      realEstatesWithPagination: realEstatesWithPagination ?? null,
    };
  }

  return {
    props: {
      device,
      pageData,
    },
  };
};

export const DetailBrokerPageContext: Context<Omit<Props, 'device'>> = createContext({});

interface Props {
  device?: 'mobile' | 'desktop';
  pageData?: {
    user?: UserUnion | null;
    realEstatesWithPagination?: ListRealEstate | null;
  } | null;
}

const DetailBrokerPage: NextPage = (props: Props) => {
  const { device, ...restProps } = props;
  const { pageData } = restProps;
  const { user } = pageData ?? {};
  const { id, fullName } = user ?? {};
  const title = fullName ? `${fullName} | ${DEFAULT_TITLE}` : DEFAULT_TITLE;
  const description = DEFAULT_DESCRIPTION;
  const ogUrl = id ? `${MAIN_DOMAIN}/${SITE_MAP.LIST_BROKER.INDEX}/${id}` : MAIN_DOMAIN;
  const ogImage = DEFAULT_OG_IMAGE;

  return (
    <Fragment>
      <Head>
        <meta
          name='viewport'
          content='width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover'
        />
        <meta name='description' content={description} />
        <meta name='og:title' content={title} />
        <meta name='og:description' content={description} />
        <meta name='og:url' content={ogUrl} />
        <meta name='og:image' content={ogImage} />
        <title>{title}</title>
      </Head>
      <DetailBrokerPageContext.Provider value={restProps}>
        {isEqual(device, 'mobile') ? (
          user ? (
            <MobileDetailBroker />
          ) : (
            <MobileError404 />
          )
        ) : isEqual(device, 'desktop') ? (
          user ? (
            <DetailBroker />
          ) : (
            <Error404 />
          )
        ) : null}
      </DetailBrokerPageContext.Provider>
    </Fragment>
  );
};

export default DetailBrokerPage;
